import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarCheck, CalendarDays, CheckCircle2, ChevronDown, ChevronRight, Loader2, Plus, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const WEEKDAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const STATUS_LABEL = {
  pending: "Pending",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
  completed: "Completed",
  no_show: "No Show",
};
const STATUS_COLOR = {
  pending: "amber",
  confirmed: "emerald",
  cancelled: "rose",
  completed: "sky",
  no_show: "slate",
};

async function api(path, token, { method = "GET", body } = {}) {
  const res = await fetch(`${API}/api${path}`, {
    method,
    headers: {
      "X-Admin-Token": token,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminScheduler({ token, onCountsChange }) {
  const [tab, setTab] = useState("upcoming");
  const [appts, setAppts] = useState([]);
  const [counts, setCounts] = useState({ upcoming: 0, today: 0, new_last_24h: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const data = await api("/admin/scheduler/appointments", token);
      setAppts(data.appointments || []);
      setCounts(data.counts || counts);
      onCountsChange?.(data.counts);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadSettings = useCallback(async () => {
    try {
      const s = await api("/admin/scheduler/settings", token);
      setSettings(s);
    } catch (e) {
      setError(e.message);
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    const now = new Date();
    return appts.filter((a) => {
      const start = new Date(a.start_time);
      if (tab === "upcoming") return start >= now && !["cancelled", "no_show"].includes(a.status);
      if (tab === "today") {
        const today = new Date();
        return start.toDateString() === today.toDateString();
      }
      if (tab === "past") return start < now || a.status === "completed";
      if (tab === "cancelled") return a.status === "cancelled" || a.status === "no_show";
      return true;
    });
  }, [appts, tab]);

  const updateStatus = async (id, status) => {
    try {
      await api(`/admin/scheduler/appointments/${id}`, token, { method: "PATCH", body: { status } });
      await load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) {
      setError(e.message);
    }
  };

  const removeAppt = async (id) => {
    if (!window.confirm("Delete this appointment permanently?")) return;
    try {
      await api(`/admin/scheduler/appointments/${id}`, token, { method: "DELETE" });
      setSelected(null);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="p-6 text-gray-900">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <CalendarDays className="text-emerald-400" size={22} />
            Scheduler
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {counts.upcoming} upcoming · {counts.today} today · {counts.new_last_24h} new in last 24h
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-open-settings"
            onClick={() => { setShowSettings(true); loadSettings(); }}
            className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-300 hover:border-emerald-500 hover:text-emerald-300 transition"
          >
            Availability
          </button>
          <button
            data-testid="admin-new-appt"
            onClick={() => setShowNew(true)}
            className="text-xs font-bold px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black flex items-center gap-1.5 transition"
          >
            <Plus size={13} /> New Appointment
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 mb-4 border-b border-gray-200 pb-2">
        {[
          { key: "upcoming", label: `Upcoming (${counts.upcoming})` },
          { key: "today", label: `Today (${counts.today})` },
          { key: "past", label: "Past" },
          { key: "cancelled", label: "Cancelled / No-show" },
          { key: "all", label: `All (${appts.length})` },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`sched-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition ${
              tab === t.key ? "bg-emerald-500/20 text-emerald-300" : "text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="mb-3 text-xs text-rose-400 border border-rose-500/40 bg-rose-500/5 rounded-lg px-3 py-2">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-emerald-400" size={22} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-gray-500 py-12 text-center border border-dashed border-gray-200 rounded-lg">
          No appointments in this bucket.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <button
              key={a.id}
              data-testid={`appt-${a.id}`}
              onClick={() => setSelected(a)}
              className="w-full text-left flex items-center gap-4 p-3 rounded-lg border border-gray-200 bg-white hover:border-emerald-500/60 transition"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold truncate">{a.client_name}</span>
                  <StatusPill status={a.status} />
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                  {a.email}{a.phone ? ` · ${a.phone}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-800">{formatDateTime(a.start_time)}</div>
                <div className="text-[11px] text-gray-500">{a.purpose}</div>
              </div>
              <ChevronRight size={16} className="text-slate-600" />
            </button>
          ))}
        </div>
      )}

      {selected ? (
        <DetailSheet
          appt={selected}
          onClose={() => setSelected(null)}
          onStatus={updateStatus}
          onDelete={removeAppt}
          onPatch={async (patch) => {
            try {
              const res = await api(`/admin/scheduler/appointments/${selected.id}`, token, { method: "PATCH", body: patch });
              setSelected(res.appointment);
              await load();
            } catch (e) { setError(e.message); }
          }}
        />
      ) : null}

      {showNew ? (
        <NewAppointment
          token={token}
          onClose={() => setShowNew(false)}
          onDone={async () => { setShowNew(false); await load(); }}
        />
      ) : null}

      {showSettings ? (
        <AvailabilityEditor
          settings={settings}
          setSettings={setSettings}
          token={token}
          onClose={() => setShowSettings(false)}
        />
      ) : null}
    </div>
  );
}

function StatusPill({ status }) {
  const c = STATUS_COLOR[status] || "slate";
  const map = {
    emerald: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
    amber: "bg-amber-500/15 border-amber-500/40 text-amber-300",
    rose: "bg-rose-500/15 border-rose-500/40 text-rose-300",
    sky: "bg-sky-500/15 border-sky-500/40 text-sky-300",
    slate: "bg-slate-700/40 border-gray-400 text-gray-700",
  };
  return (
    <span className={`text-[10px] font-bold tracking-[0.15em] uppercase px-1.5 py-0.5 rounded border ${map[c]}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function Sheet({ children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white/80" onClick={onClose} data-testid={testId}>
      <div className="max-w-lg w-full bg-white border border-gray-200 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-900" onClick={onClose} data-testid="sheet-close">
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

function DetailSheet({ appt, onClose, onStatus, onDelete, onPatch }) {
  const [notes, setNotes] = useState(appt.notes || "");
  useEffect(() => setNotes(appt.notes || ""), [appt.id]);
  return (
    <Sheet onClose={onClose} testId="appt-detail-sheet">
      <div className="flex items-center gap-2 mb-1">
        <CalendarCheck className="text-emerald-400" size={20} />
        <h3 className="text-lg font-bold">{appt.client_name}</h3>
        <StatusPill status={appt.status} />
      </div>
      <div className="text-xs text-gray-500 mb-4">{appt.email}{appt.phone ? ` · ${appt.phone}` : ""}</div>

      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
        <Info label="Time" value={formatDateTime(appt.start_time)} />
        <Info label="Purpose" value={appt.purpose} />
        <Info label="Source" value={appt.source || "public"} />
        <Info label="Reminders" value={(appt.reminders_sent || []).join(", ") || "None yet"} />
      </div>

      <label className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase block mb-1">Internal notes</label>
      <textarea
        data-testid="appt-notes"
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={() => notes !== (appt.notes || "") && onPatch({ notes })}
        className="w-full rounded-lg bg-white border border-gray-300 px-2.5 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500 mb-4"
      />

      <div className="flex flex-wrap gap-2">
        {["confirmed", "completed", "no_show", "cancelled"].map((s) => (
          <button
            key={s}
            data-testid={`set-${s}`}
            onClick={() => onStatus(appt.id, s)}
            className={`text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition ${
              appt.status === s
                ? "bg-emerald-500/20 border-emerald-500 text-emerald-300"
                : "border-gray-300 text-gray-700 hover:border-emerald-500"
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
        <div className="ml-auto">
          <button
            data-testid="appt-delete"
            onClick={() => onDelete(appt.id)}
            className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-rose-500/50 text-rose-300 hover:bg-rose-500/10 transition"
          >
            Delete
          </button>
        </div>
      </div>
    </Sheet>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase">{label}</div>
      <div className="text-sm text-gray-900 mt-0.5">{value}</div>
    </div>
  );
}

function NewAppointment({ token, onClose, onDone }) {
  const [form, setForm] = useState({ client_name: "", email: "", phone: "", purpose: "Pre-approval consultation" });
  const [when, setWhen] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    if (!form.client_name || !form.email || !when) {
      setErr("Name, email and date/time are required.");
      return;
    }
    setSaving(true);
    try {
      const iso = new Date(when).toISOString();
      await api("/admin/scheduler/appointments", token, {
        method: "POST",
        body: { ...form, start_time_iso: iso },
      });
      onDone();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet onClose={onClose} testId="new-appt-sheet">
      <h3 className="text-lg font-bold mb-4">New Appointment</h3>
      <div className="space-y-3">
        <TextRow label="Client name" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} testId="new-name" />
        <TextRow label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="new-email" />
        <TextRow label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="new-phone" />
        <TextRow label="Purpose" value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v })} testId="new-purpose" />
        <div>
          <label className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase block mb-1">Date & time</label>
          <input
            data-testid="new-when"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="w-full rounded-lg bg-white border border-gray-300 px-2.5 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500"
          />
        </div>
        {err ? <p className="text-xs text-rose-400">{err}</p> : null}
        <button
          data-testid="new-save"
          onClick={submit}
          disabled={saving}
          className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm py-2.5 flex items-center justify-center gap-2 transition disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Create appointment
        </button>
      </div>
    </Sheet>
  );
}

function TextRow({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase block mb-1">{label}</label>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-white border border-gray-300 px-2.5 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500"
      />
    </div>
  );
}

function AvailabilityEditor({ settings, setSettings, token, onClose }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [expandedDay, setExpandedDay] = useState("mon");

  if (!settings) {
    return (
      <Sheet onClose={onClose} testId="avail-sheet">
        <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-emerald-400" /></div>
      </Sheet>
    );
  }

  const patch = (updates) => setSettings({ ...settings, ...updates });
  const setWeeklyDay = (key, windows) => setSettings({ ...settings, weekly: { ...settings.weekly, [key]: windows } });

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      await api("/admin/scheduler/settings", token, { method: "PUT", body: settings });
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet onClose={onClose} testId="avail-sheet">
      <h3 className="text-lg font-bold mb-4">Availability</h3>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <NumberRow label="Slot duration (min)" value={settings.slot_minutes} onChange={(v) => patch({ slot_minutes: Number(v) || 30 })} testId="slot-min" />
        <NumberRow label="Lead time (hrs)" value={settings.lead_time_hours} onChange={(v) => patch({ lead_time_hours: Number(v) || 0 })} testId="lead-time" />
        <NumberRow label="Horizon (days)" value={settings.horizon_days} onChange={(v) => patch({ horizon_days: Number(v) || 30 })} testId="horizon" />
        <TextRow label="Timezone" value={settings.timezone} onChange={(v) => patch({ timezone: v })} testId="tz" />
      </div>

      <div className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase mb-1">Weekly template</div>
      <div className="space-y-1 mb-4">
        {WEEKDAYS.map(({ key, label }) => {
          const windows = settings.weekly?.[key] || [];
          const open = expandedDay === key;
          return (
            <div key={key} className="rounded-lg border border-gray-200">
              <button
                onClick={() => setExpandedDay(open ? "" : key)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm"
                data-testid={`weekday-${key}`}
              >
                <span className="font-semibold">{label}</span>
                <span className="text-xs text-gray-500">
                  {windows.length === 0 ? "Closed" : windows.map((w) => `${w.start}–${w.end}`).join(", ")}
                  <ChevronDown size={14} className={`inline ml-1 transition ${open ? "rotate-180" : ""}`} />
                </span>
              </button>
              {open ? (
                <div className="p-3 border-t border-gray-200 space-y-2">
                  {windows.map((w, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        data-testid={`w-${key}-${idx}-start`}
                        type="time"
                        value={w.start}
                        onChange={(e) => {
                          const nxt = [...windows]; nxt[idx] = { ...w, start: e.target.value };
                          setWeeklyDay(key, nxt);
                        }}
                        className="rounded-lg bg-white border border-gray-300 px-2 py-1 text-xs text-gray-900 outline-none focus:border-emerald-500"
                      />
                      <span className="text-gray-500">→</span>
                      <input
                        data-testid={`w-${key}-${idx}-end`}
                        type="time"
                        value={w.end}
                        onChange={(e) => {
                          const nxt = [...windows]; nxt[idx] = { ...w, end: e.target.value };
                          setWeeklyDay(key, nxt);
                        }}
                        className="rounded-lg bg-white border border-gray-300 px-2 py-1 text-xs text-gray-900 outline-none focus:border-emerald-500"
                      />
                      <button
                        onClick={() => setWeeklyDay(key, windows.filter((_, i) => i !== idx))}
                        className="ml-auto text-rose-400 hover:text-rose-300 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    data-testid={`w-${key}-add`}
                    onClick={() => setWeeklyDay(key, [...windows, { start: "09:00", end: "17:00" }])}
                    className="text-xs font-semibold text-emerald-300 hover:text-emerald-200"
                  >
                    + Add window
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase mb-1">Blocked dates (YYYY-MM-DD, comma separated)</div>
      <input
        data-testid="blocked-dates"
        value={(settings.blocked_dates || []).join(", ")}
        onChange={(e) =>
          patch({
            blocked_dates: e.target.value
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean),
          })
        }
        className="w-full rounded-lg bg-white border border-gray-300 px-2.5 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500 mb-4"
      />

      <div className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase mb-1">Purpose options (comma separated)</div>
      <input
        data-testid="purposes"
        value={(settings.purposes || []).join(", ")}
        onChange={(e) =>
          patch({
            purposes: e.target.value
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean),
          })
        }
        className="w-full rounded-lg bg-white border border-gray-300 px-2.5 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500 mb-4"
      />

      {err ? <p className="text-xs text-rose-400 mb-2">{err}</p> : null}
      <button
        data-testid="avail-save"
        onClick={save}
        disabled={saving}
        className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm py-2.5 flex items-center justify-center gap-2 transition disabled:opacity-60"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
        Save availability
      </button>
    </Sheet>
  );
}

function NumberRow({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase block mb-1">{label}</label>
      <input
        data-testid={testId}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-white border border-gray-300 px-2.5 py-2 text-sm text-gray-900 outline-none focus:border-emerald-500"
      />
    </div>
  );
}
